import { crewIdFromName } from "./duty";
import { DEFAULT_PILOT, HAWKER_ICAO_TYPE, normalizeRatedTypes } from "./profile";
import { inviteMatches, unionRemovedCrewIds } from "./company";
import { hydrateCrewRoutePrefs } from "./crewPrefs";
import type {
  CompanyProfile,
  CrewMember,
  CrewPosition,
  DeskRole,
  PilotProfile,
  PilotScheduleView,
  SceneryPresence,
} from "./types";

export const CREW_POSITIONS: CrewPosition[] = ["pic", "sic", "cabin", "other"];

export function hydrateCrewPosition(raw: unknown): CrewPosition {
  return raw === "pic" || raw === "sic" || raw === "cabin" || raw === "other" ? raw : "other";
}

export function crewPositionLabel(position: CrewPosition | undefined): string {
  switch (position) {
    case "pic":
      return "PIC";
    case "sic":
      return "SIC";
    case "cabin":
      return "Cabin";
    default:
      return "Other";
  }
}

/** Soft cap for roster photo data URLs so the Nanode snapshot stays lean. */
export const CREW_PHOTO_MAX_DATA_URL_CHARS = 48_000;

export function isUsableCrewPhotoDataUrl(raw: unknown): raw is string {
  if (typeof raw !== "string") return false;
  const t = raw.trim();
  if (!t || t.length > CREW_PHOTO_MAX_DATA_URL_CHARS) return false;
  return /^data:image\/(jpeg|jpg|png|webp);base64,/i.test(t);
}

export function hydrateCrewPhoto(raw: unknown): string {
  return isUsableCrewPhotoDataUrl(raw) ? raw.trim() : "";
}

export function hydratePilotScheduleView(raw: unknown): PilotScheduleView {
  return raw === "company" ? "company" : "mine";
}

export function newCrewId(name: string, existing: string[] = []): string {
  const base = crewIdFromName(name);
  if (!existing.includes(base)) return base;
  return `${base}-${Date.now().toString(36).slice(-4)}`;
}

export function newCrewMember(input: {
  displayName: string;
  roles: DeskRole[];
  isAdmin?: boolean;
  ratedIcaoTypes?: string[];
  communityFolderPath?: string;
  sceneryGateEnabled?: boolean;
  phone?: string;
  email?: string;
  callsign?: string;
  employeeId?: string;
  homeBaseIcao?: string;
  position?: CrewPosition;
  certificatesNotes?: string;
  medicalNotes?: string;
  notes?: string;
  profilePictureDataUrl?: string;
  now?: Date;
  id?: string;
}): CrewMember {
  const now = input.now ?? new Date();
  const displayName = input.displayName.trim() || DEFAULT_PILOT.name;
  const roles = input.roles.length ? input.roles : (["pilot"] as DeskRole[]);
  return {
    id: input.id || crewIdFromName(displayName),
    displayName,
    roles: [...new Set(roles)],
    isAdmin: Boolean(input.isAdmin),
    communityFolderPath: input.communityFolderPath?.trim() ?? "",
    contentXmlPath: "",
    sceneryInventory: [],
    sceneryGateEnabled: input.sceneryGateEnabled !== false,
    pilotScheduleView: "mine",
    ratedIcaoTypes: Array.isArray(input.ratedIcaoTypes)
      ? [...new Set(input.ratedIcaoTypes.map((type) => type.trim().toUpperCase()).filter(Boolean))]
      : normalizeRatedTypes(undefined),
    createdAtUtc: now.toISOString(),
    pinSalt: "",
    pinHash: "",
    phone: typeof input.phone === "string" ? input.phone.trim() : "",
    email: typeof input.email === "string" ? input.email.trim() : "",
    callsign: typeof input.callsign === "string" ? input.callsign.trim() : "",
    employeeId: typeof input.employeeId === "string" ? input.employeeId.trim() : "",
    homeBaseIcao: typeof input.homeBaseIcao === "string" ? input.homeBaseIcao.trim().toUpperCase() : "",
    position: input.position ? hydrateCrewPosition(input.position) : "other",
    certificatesNotes: typeof input.certificatesNotes === "string" ? input.certificatesNotes.trim() : "",
    medicalNotes: typeof input.medicalNotes === "string" ? input.medicalNotes.trim() : "",
    notes: typeof input.notes === "string" ? input.notes.trim() : "",
    profilePictureDataUrl: hydrateCrewPhoto(input.profilePictureDataUrl),
    routeAreaPrefs: "",
    preferredTripNm: null,
  };
}

export function hydrateCrewMember(raw?: Partial<CrewMember> | null): CrewMember | null {
  if (!raw || typeof raw !== "object") return null;
  const displayName = raw.displayName?.trim() || raw.id?.trim();
  if (!displayName && !raw.id) return null;
  const roles = Array.isArray(raw.roles)
    ? raw.roles.filter((r): r is DeskRole => r === "dispatcher" || r === "pilot")
    : [];
  const inventory = Array.isArray(raw.sceneryInventory)
    ? raw.sceneryInventory.filter((row) => row && typeof row.icao === "string")
    : [];
  return {
    id: raw.id?.trim() || crewIdFromName(displayName || DEFAULT_PILOT.name),
    displayName: displayName || DEFAULT_PILOT.name,
    roles: roles.length ? [...new Set(roles)] : ["pilot"],
    isAdmin: Boolean(raw.isAdmin),
    communityFolderPath: typeof raw.communityFolderPath === "string" ? raw.communityFolderPath : "",
    contentXmlPath: typeof raw.contentXmlPath === "string" ? raw.contentXmlPath.trim() : "",
    sceneryInventory: inventory.map((row) => ({
      icao: row.icao.trim().toUpperCase(),
      source: row.source === "manual" ? "manual" : row.source === "manifest" ? "manifest" : "folder_name",
      folderName: row.folderName,
      scannedAtUtc: row.scannedAtUtc || new Date().toISOString(),
    })),
    sceneryGateEnabled: raw.sceneryGateEnabled !== false,
    pilotScheduleView: hydratePilotScheduleView(raw.pilotScheduleView),
    ratedIcaoTypes: normalizeRatedTypes(raw.ratedIcaoTypes),
    createdAtUtc: raw.createdAtUtc || new Date().toISOString(),
    pinSalt: typeof raw.pinSalt === "string" ? raw.pinSalt : "",
    pinHash: typeof raw.pinHash === "string" ? raw.pinHash : "",
    phone: typeof raw.phone === "string" ? raw.phone.trim() : "",
    email: typeof raw.email === "string" ? raw.email.trim() : "",
    callsign: typeof raw.callsign === "string" ? raw.callsign.trim() : "",
    employeeId: typeof raw.employeeId === "string" ? raw.employeeId.trim() : "",
    homeBaseIcao:
      typeof raw.homeBaseIcao === "string" ? raw.homeBaseIcao.trim().toUpperCase() : "",
    position: hydrateCrewPosition(raw.position),
    certificatesNotes: typeof raw.certificatesNotes === "string" ? raw.certificatesNotes.trim() : "",
    medicalNotes: typeof raw.medicalNotes === "string" ? raw.medicalNotes.trim() : "",
    notes: typeof raw.notes === "string" ? raw.notes.trim() : "",
    profilePictureDataUrl: hydrateCrewPhoto(raw.profilePictureDataUrl),
    routeAreaPrefs: hydrateCrewRoutePrefs(raw).routeAreaPrefs,
    preferredTripNm: hydrateCrewRoutePrefs(raw).preferredTripNm,
    payPerHour:
      typeof raw.payPerHour === "number" && Number.isFinite(raw.payPerHour) && raw.payPerHour > 0
        ? Math.round(raw.payPerHour * 100) / 100
        : undefined,
  };
}

export function seedCrewFromProfile(profile?: PilotProfile, now = new Date()): CrewMember[] {
  const p = profile ?? DEFAULT_PILOT;
  return [
    newCrewMember({
      displayName: p.name,
      roles: ["dispatcher", "pilot"],
      isAdmin: true,
      ratedIcaoTypes: p.ratedIcaoTypes,
      now,
      id: crewIdFromName(p.name),
    }),
  ];
}

export function hydrateCrewRoster(
  raw: unknown,
  profile?: PilotProfile,
  opts?: { seedIfEmpty?: boolean },
): CrewMember[] {
  const seed = opts?.seedIfEmpty !== false;
  if (!Array.isArray(raw)) {
    return seed ? seedCrewFromProfile(profile) : [];
  }
  const out: CrewMember[] = [];
  const seen = new Set<string>();
  for (const row of raw) {
    const member = hydrateCrewMember(row as Partial<CrewMember>);
    if (!member || seen.has(member.id)) continue;
    seen.add(member.id);
    out.push(member);
  }
  if (!out.length && seed) return seedCrewFromProfile(profile);
  return out;
}

export function crewById(roster: CrewMember[], id: string | null | undefined): CrewMember | undefined {
  if (!id) return undefined;
  return roster.find((c) => c.id === id);
}

export function upsertCrewMember(roster: CrewMember[], member: CrewMember): CrewMember[] {
  const idx = roster.findIndex((c) => c.id === member.id);
  if (idx === -1) return [...roster, member];
  return roster.map((c, i) => (i === idx ? member : c));
}

export function patchCrewMember(roster: CrewMember[], id: string, patch: Partial<CrewMember>): CrewMember[] {
  return roster.map((c) => {
    if (c.id !== id) return c;
    return hydrateCrewMember({ ...c, ...patch, id: c.id }) ?? c;
  });
}

export function applyScanToCrew(
  member: CrewMember,
  inventory: SceneryPresence[],
  path?: string,
  contentXmlPath?: string,
): CrewMember {
  return {
    ...member,
    communityFolderPath: path !== undefined ? path : member.communityFolderPath,
    contentXmlPath: contentXmlPath !== undefined ? contentXmlPath.trim() : member.contentXmlPath,
    sceneryInventory: inventory,
  };
}

/**
 * Per-desk Content.xml / Community scan + Pilot schedule view stay on the
 * machine that scanned them. Pull must not let another desk’s empty or stale
 * inventory overwrite this sign-on.
 */
export function mergeCrewLocalScenery(incoming: CrewMember[], local: CrewMember[]): CrewMember[] {
  const localById = new Map(local.map((row) => [row.id, row]));
  return incoming.map((member) => {
    const prior = localById.get(member.id);
    if (!prior) return member;
    return {
      ...member,
      sceneryInventory: prior.sceneryInventory,
      communityFolderPath: prior.communityFolderPath,
      contentXmlPath: prior.contentXmlPath,
      sceneryGateEnabled: prior.sceneryGateEnabled,
      pilotScheduleView: prior.pilotScheduleView,
    };
  });
}

export function profileFromCrew(member: CrewMember, fallback?: PilotProfile): PilotProfile {
  const base = fallback ?? DEFAULT_PILOT;
  return {
    ...base,
    name: member.displayName,
    pilotNumber: member.employeeId?.trim() || base.pilotNumber,
    phone: member.phone?.trim() || base.phone,
    email: member.email?.trim() || base.email,
    ratedIcaoTypes: normalizeRatedTypes(member.ratedIcaoTypes.length ? member.ratedIcaoTypes : base.ratedIcaoTypes),
  };
}

export function crewRolesLabel(roles: DeskRole[]): string {
  const set = new Set(roles);
  if (set.has("dispatcher") && set.has("pilot")) return "Dispatcher · Pilot";
  if (set.has("dispatcher")) return "Dispatcher";
  return "Pilot";
}

export type JoinCrewInput = {
  inviteCode: string;
  displayName: string;
  role: DeskRole;
  /** Optional hashed desk PIN (UI hashes before calling). */
  pinSalt?: string;
  pinHash?: string;
};

export type JoinCrewResult =
  | { ok: true; member: CrewMember; roster: CrewMember[] }
  | { ok: false; error: string };

export function joinCrew(
  roster: CrewMember[],
  company: CompanyProfile,
  input: JoinCrewInput,
  now = new Date(),
): JoinCrewResult {
  if (!company.setupComplete) {
    return { ok: false, error: "No company is configured on this desk yet." };
  }
  if (!inviteMatches(company, input.inviteCode)) {
    return { ok: false, error: "Invite code does not match this company." };
  }
  const displayName = input.displayName.trim();
  if (!displayName) {
    return { ok: false, error: "Enter a crew name." };
  }
  const role: DeskRole = input.role === "dispatcher" ? "dispatcher" : "pilot";
  const reserved = [...roster.map((c) => c.id), ...unionRemovedCrewIds(company.removedCrewIds)];
  const id = newCrewId(displayName, reserved);
  const member = newCrewMember({
    displayName,
    roles: [role],
    isAdmin: false,
    ratedIcaoTypes: [HAWKER_ICAO_TYPE],
    now,
    id,
  });
  if (input.pinSalt?.trim() && input.pinHash?.trim()) {
    member.pinSalt = input.pinSalt;
    member.pinHash = input.pinHash;
  }
  return { ok: true, member, roster: [...roster, member] };
}

export type AddCrewInput = {
  displayName: string;
  roles?: DeskRole[];
  position?: CrewPosition;
  callsign?: string;
  employeeId?: string;
  phone?: string;
  email?: string;
  homeBaseIcao?: string;
};

export type AddCrewResult =
  | { ok: true; member: CrewMember; roster: CrewMember[] }
  | { ok: false; error: string };

/** Dispatcher roster add — no invite code. Joins still use {@link joinCrew}. */
export function addLocalCrewMember(
  roster: CrewMember[],
  company: CompanyProfile,
  input: AddCrewInput,
  now = new Date(),
): AddCrewResult {
  if (!company.setupComplete) {
    return { ok: false, error: "No company is configured on this desk yet." };
  }
  const displayName = input.displayName.trim();
  if (!displayName) {
    return { ok: false, error: "Enter a crew name." };
  }
  const roles = (input.roles?.length ? input.roles : (["pilot"] as DeskRole[])).filter(
    (r): r is DeskRole => r === "dispatcher" || r === "pilot",
  );
  const reserved = [...roster.map((c) => c.id), ...unionRemovedCrewIds(company.removedCrewIds)];
  const id = newCrewId(displayName, reserved);
  const member = newCrewMember({
    displayName,
    roles: roles.length ? roles : ["pilot"],
    isAdmin: false,
    ratedIcaoTypes: [HAWKER_ICAO_TYPE],
    phone: input.phone,
    email: input.email,
    callsign: input.callsign,
    employeeId: input.employeeId,
    homeBaseIcao: input.homeBaseIcao,
    position: input.position,
    now,
    id,
  });
  return { ok: true, member, roster: [...roster, member] };
}

export function currentCrew(roster: CrewMember[], currentId: string | null | undefined): CrewMember | undefined {
  return crewById(roster, currentId) ?? roster.find((c) => c.isAdmin) ?? roster[0];
}

/** Pilots who can take a trip. Dispatcher-only roster members are not assigned flying work. */
export function assignablePilots(roster: CrewMember[]): CrewMember[] {
  return roster.filter((c) => c.roles.includes("pilot"));
}

/**
 * Union two rosters by id. Incoming wins on overlap; members only on
 * `existing` are kept so a stale Push cannot wipe a remote Join.
 * Tombstoned ids (remove / leave) stay out of the result.
 */
export function mergeCrewRosters(
  incoming: CrewMember[],
  existing: CrewMember[],
  removedIds: string[] = [],
): CrewMember[] {
  const tomb = new Set(unionRemovedCrewIds(removedIds));
  const byId = new Map<string, CrewMember>();
  for (const member of existing) {
    if (tomb.has(member.id)) continue;
    byId.set(member.id, member);
  }
  for (const member of incoming) {
    if (tomb.has(member.id)) continue;
    const prev = byId.get(member.id);
    byId.set(member.id, prev ? { ...prev, ...member, id: member.id } : member);
  }
  return [...byId.values()];
}

export type RemoveCrewResult =
  | {
      ok: true;
      roster: CrewMember[];
      removedCrewIds: string[];
      member: CrewMember;
      promotedId?: string;
    }
  | { ok: false; error: string };

/**
 * Drop a roster row and tombstone the id. Does not wipe the company.
 * Kick (dispatcher) cannot target self or empty the roster — use leave for that.
 */
export function removeCrewMember(
  roster: CrewMember[],
  removedCrewIds: string[],
  crewId: string,
  opts?: { allowLast?: boolean; actorId?: string },
): RemoveCrewResult {
  const id = crewId.trim();
  const member = roster.find((c) => c.id === id);
  if (!member) return { ok: false, error: "That crew member is not on this company roster." };
  if (opts?.actorId && opts.actorId === id && !opts.allowLast) {
    return { ok: false, error: "You cannot remove yourself. Use Leave company." };
  }
  const remaining = roster.filter((c) => c.id !== id);
  if (!remaining.length && !opts?.allowLast) {
    return { ok: false, error: "Cannot remove the last crew member. They can Leave company instead." };
  }
  let nextRoster = remaining;
  let promotedId: string | undefined;
  if (member.isAdmin && remaining.length && !remaining.some((c) => c.isAdmin)) {
    const pick = [...remaining].sort((a, b) => a.createdAtUtc.localeCompare(b.createdAtUtc))[0]!;
    nextRoster = remaining.map((c) => (c.id === pick.id ? { ...c, isAdmin: true } : c));
    promotedId = pick.id;
  }
  return {
    ok: true,
    roster: nextRoster,
    removedCrewIds: unionRemovedCrewIds(removedCrewIds, [id]),
    member,
    promotedId,
  };
}

/** True when this desk's signed-on crew was kicked or left on the incoming snapshot. */
export function localCrewWasRemoved(input: {
  localCrewId?: string | null;
  localCompanyId?: string | null;
  incomingCompanyId?: string | null;
  incomingRoster: CrewMember[];
  incomingRemovedIds?: string[] | null;
}): boolean {
  const id = input.localCrewId?.trim();
  if (!id) return false;
  const localCo = input.localCompanyId?.trim() ?? "";
  const incomingCo = input.incomingCompanyId?.trim() ?? "";
  if (localCo && incomingCo && localCo !== incomingCo) return false;
  if (unionRemovedCrewIds(input.incomingRemovedIds).includes(id)) return true;
  return input.incomingRoster.length > 0 && !input.incomingRoster.some((c) => c.id === id);
}
